/*
 * hitched-form-embed.js — the storefront renderer for a Bench marketing
 * form (Marketing → Forms in Bench).
 *
 * Hand-written vanilla JS, NO build step: this file is the source AND the
 * deploy artifact, uploaded to the theme as-is via themeFilesUpsert. Keep
 * it readable — the next person editing it is doing so at 5pm before a
 * campaign goes out.
 *
 * What it does:
 *   1. finds every <div data-hitched-form data-form-slug="…">,
 *   2. GETs  {apiBase}/forms/{slug}          → the form definition,
 *   3. renders neutral `hf-*` markup dressed by the section's own
 *      stylesheet (assets/hitched-form.css) — the CSS injected from HERE
 *      stays structural (no colors, no fonts, no radii) and exists only
 *      as a safety net for a page that got the JS but not the CSS; the
 *      design panel in Bench styles the HOSTED page, never this one,
 *   4. POSTs {apiBase}/forms/{slug}/submit   → { ok: true } | { errors },
 *   5. shows the success message, or follows the form's redirect.
 *
 * The pure half is exposed as window.HitchedForms._core so jest can test
 * it without a DOM (same pattern as the stone-search widget).
 */
(function () {
  'use strict';

  var HONEYPOT_NAME = '_hp';
  var EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'];
  var DEFAULT_SUCCESS = 'Thank you — we have your details and will be in touch shortly.';
  var DEFAULT_BUTTON_LABEL = 'Submit';

  /* Structural only — a fallback so the form is still usable if
     assets/hitched-form.css didn't make it onto the theme. Every visual
     decision (color, type, borders, the card) lives in that stylesheet,
     whose selectors carry two classes and therefore win over these. */
  var CSS = [
    '.hf-form{display:block;max-width:34rem;margin:0 auto}',
    '.hf-field{display:block;margin-bottom:1rem}',
    '.hf-label{display:block;margin-bottom:.25rem}',
    '.hf-input,.hf-textarea,.hf-select{display:block;width:100%;box-sizing:border-box}',
    '.hf-textarea{min-height:6rem}',
    '.hf-checkbox-row{display:flex;align-items:flex-start;gap:.5rem}',
    '.hf-checkbox-row .hf-label{margin-bottom:0}',
    '.hf-error{display:block;margin-top:.25rem;font-size:.875em}',
    '.hf-hp{position:absolute!important;left:-9999px!important;width:1px;height:1px;opacity:0}',
    '.hf-consent{margin:1rem 0}',
    '.hf-actions{margin-top:1rem}',
    '.hf-busy{opacity:.6}'
  ].join('');

  // ── pure core (unit-tested) ───────────────────────────────────────

  function str(v) {
    return v == null ? '' : String(v);
  }

  function esc(v) {
    return str(v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fieldList(def) {
    return def && Object.prototype.toString.call(def.fields) === '[object Array]' ? def.fields : [];
  }

  /**
   * Same rules the server enforces (src/lib/marketing-forms.ts): required
   * fields, a parseable email, a select value that is one of its own
   * options. Returns [{ key, message }] — empty means "send it".
   */
  function validate(fields, values) {
    var errors = [];
    (fields || []).forEach(function (field) {
      var value = values ? values[field.key] : undefined;
      if (field.type === 'checkbox') {
        if (field.required && value !== true) {
          errors.push({ key: field.key, message: field.label + ' is required' });
        }
        return;
      }
      var text = typeof value === 'string' ? value.trim() : '';
      if (!text) {
        if (field.required) errors.push({ key: field.key, message: field.label + ' is required' });
        return;
      }
      if (field.type === 'email' && !EMAIL_RX.test(text.toLowerCase())) {
        errors.push({ key: field.key, message: 'Enter a valid email address' });
      }
      if (field.type === 'select') {
        var options = field.options || [];
        if (options.indexOf(text) === -1) {
          errors.push({ key: field.key, message: field.label + ' must be one of the listed options' });
        }
      }
    });
    return errors;
  }

  /** utm_* off a query string, in the shape the submit handler keeps. */
  function readUtm(search) {
    var out = {};
    var query = str(search).replace(/^\?/, '');
    if (!query) return null;
    query.split('&').forEach(function (pair) {
      if (!pair) return;
      var bits = pair.split('=');
      var key = decodeURIComponent(bits[0] || '').replace(/\+/g, ' ');
      var value = decodeURIComponent((bits[1] || '').replace(/\+/g, ' '));
      if (key.indexOf('utm_') !== 0) return;
      var name = key.slice(4);
      if (UTM_KEYS.indexOf(name) === -1) return;
      value = value.trim().slice(0, 200);
      if (value) out[name] = value;
    });
    return Object.keys(out).length ? out : null;
  }

  /**
   * The JSON body posted to Bench. Only the form's own field keys ride
   * along (plus the honeypot, the consent tick and any UTMs) — the server
   * drops anything else, and sending it anyway just makes the two ends
   * look like they disagree.
   */
  function buildPayload(def, values, extras) {
    var payload = {};
    fieldList(def).forEach(function (field) {
      var value = values ? values[field.key] : undefined;
      payload[field.key] = field.type === 'checkbox' ? value === true : str(value);
    });
    payload[HONEYPOT_NAME] = str(extras && extras.honeypot);
    if (def && def.collectMarketingConsent) {
      payload.consent = !!(extras && extras.consent);
    }
    var utm = extras && extras.utm;
    if (utm && Object.keys(utm).length) payload.utm = utm;
    return payload;
  }

  /** Map a server error list onto field keys (paths arrive as `values.x`). */
  function errorsByKey(errors) {
    var out = {};
    (errors || []).forEach(function (err) {
      if (!err) return;
      var key = str(err.path).replace(/^values\./, '');
      if (key && !out[key]) out[key] = str(err.message);
    });
    return out;
  }

  /** Only http(s) redirects are ever followed. */
  function safeRedirect(url) {
    var value = str(url).trim();
    return /^https?:\/\//i.test(value) || value.charAt(0) === '/' ? value : null;
  }

  var core = {
    validate: validate,
    buildPayload: buildPayload,
    readUtm: readUtm,
    errorsByKey: errorsByKey,
    safeRedirect: safeRedirect,
    esc: esc,
    fieldList: fieldList,
    HONEYPOT_NAME: HONEYPOT_NAME,
    DEFAULT_SUCCESS: DEFAULT_SUCCESS,
    DEFAULT_BUTTON_LABEL: DEFAULT_BUTTON_LABEL
  };

  // ── DOM half ──────────────────────────────────────────────────────

  function injectCss() {
    if (document.getElementById('hitched-form-css')) return;
    var style = document.createElement('style');
    style.id = 'hitched-form-css';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function inputType(type) {
    if (type === 'email') return 'email';
    if (type === 'phone') return 'tel';
    if (type === 'date') return 'date';
    return 'text';
  }

  function fieldHtml(slug, field) {
    var id = 'hf-' + slug + '-' + field.key;
    var required = field.required ? ' required' : '';
    var star = field.required ? ' <span class="hf-required" aria-hidden="true">*</span>' : '';
    var placeholder = field.placeholder ? ' placeholder="' + esc(field.placeholder) + '"' : '';

    if (field.type === 'checkbox') {
      return (
        '<div class="hf-field" data-hf-field="' + esc(field.key) + '">' +
        '<div class="hf-checkbox-row">' +
        '<input class="hf-checkbox" type="checkbox" id="' + esc(id) + '" name="' + esc(field.key) + '"' + required + '>' +
        '<label class="hf-label" for="' + esc(id) + '">' + esc(field.label) + star + '</label>' +
        '</div><span class="hf-error" data-hf-error="' + esc(field.key) + '"></span></div>'
      );
    }

    var control;
    if (field.type === 'textarea') {
      control = '<textarea class="hf-textarea" id="' + esc(id) + '" name="' + esc(field.key) + '" rows="4"' + placeholder + required + '></textarea>';
    } else if (field.type === 'select') {
      var options = (field.options || []).map(function (option) {
        return '<option value="' + esc(option) + '">' + esc(option) + '</option>';
      }).join('');
      control =
        '<select class="hf-select" id="' + esc(id) + '" name="' + esc(field.key) + '"' + required + '>' +
        '<option value="">' + esc(field.placeholder || 'Please choose…') + '</option>' + options + '</select>';
    } else {
      control = '<input class="hf-input" type="' + inputType(field.type) + '" id="' + esc(id) + '" name="' + esc(field.key) + '"' + placeholder + required + '>';
    }

    return (
      '<div class="hf-field" data-hf-field="' + esc(field.key) + '">' +
      '<label class="hf-label" for="' + esc(id) + '">' + esc(field.label) + star + '</label>' +
      control +
      '<span class="hf-error" data-hf-error="' + esc(field.key) + '"></span></div>'
    );
  }

  function formHtml(def, opts) {
    var slug = esc(def.slug);
    var heading = (opts && opts.heading) || def.title;
    var html = '';
    if (heading) html += '<h2 class="hf-title">' + esc(heading) + '</h2>';
    if (def.description) html += '<p class="hf-description">' + esc(def.description) + '</p>';
    html += '<form class="hf-form" novalidate>';
    fieldList(def).forEach(function (field) {
      html += fieldHtml(slug, field);
    });
    if (def.collectMarketingConsent) {
      html +=
        '<div class="hf-consent"><div class="hf-checkbox-row">' +
        '<input class="hf-checkbox" type="checkbox" id="hf-' + slug + '-consent" name="consent" data-hf-consent>' +
        '<label class="hf-label" for="hf-' + slug + '-consent">' + esc(def.consentDisclosure || '') + '</label>' +
        '</div></div>';
    }
    /* Honeypot: off-screen, not tabbable, never announced. */
    html += '<input class="hf-hp" type="text" name="' + HONEYPOT_NAME + '" tabindex="-1" autocomplete="off" aria-hidden="true">';
    html += '<p class="hf-error hf-form-error" role="alert" data-hf-form-error></p>';
    html += '<div class="hf-actions"><button class="hf-button button" type="submit">' + esc(DEFAULT_BUTTON_LABEL) + '</button></div>';
    html += '</form>';
    return html;
  }

  function readValues(root, def) {
    var values = {};
    fieldList(def).forEach(function (field) {
      var el = root.querySelector('[name="' + field.key.replace(/"/g, '') + '"]');
      if (!el) {
        values[field.key] = field.type === 'checkbox' ? false : '';
        return;
      }
      values[field.key] = field.type === 'checkbox' ? !!el.checked : el.value;
    });
    return values;
  }

  function paintErrors(root, byKey) {
    var slots = root.querySelectorAll('[data-hf-error]');
    for (var i = 0; i < slots.length; i++) {
      var key = slots[i].getAttribute('data-hf-error');
      slots[i].textContent = byKey[key] || '';
    }
  }

  function mount(el) {
    if (el.getAttribute('data-hf-mounted')) return;
    el.setAttribute('data-hf-mounted', '1');

    var slug = el.getAttribute('data-form-slug');
    var apiBase = (el.getAttribute('data-api-base') || '/apps/stones/api').replace(/\/+$/, '');
    var heading = el.getAttribute('data-heading') || null;
    if (!slug) return;

    injectCss();
    /* A styleable node, not a bare text node — the stylesheet quiets it
       (.hf-loading) instead of dropping raw text into the card. */
    el.innerHTML = '<p class="hf-loading">Loading…</p>';

    fetch(apiBase + '/forms/' + encodeURIComponent(slug), { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        var def = body && body.form;
        if (!def) {
          /* A form that isn't published must not leave a broken block on
             the page — render nothing at all. */
          el.textContent = '';
          return;
        }
        el.innerHTML = formHtml(def, { heading: heading });
        wire(el, def, apiBase);
      })
      .catch(function () {
        el.textContent = '';
      });
  }

  function wire(el, def, apiBase) {
    var form = el.querySelector('form');
    if (!form) return;
    var formError = el.querySelector('[data-hf-form-error]');
    var button = el.querySelector('.hf-button');

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (form.getAttribute('data-hf-busy')) return;

      var values = readValues(el, def);
      var problems = validate(fieldList(def), values);
      if (problems.length) {
        paintErrors(el, errorsByKey(problems.map(function (p) { return { path: p.key, message: p.message }; })));
        return;
      }
      paintErrors(el, {});
      if (formError) formError.textContent = '';

      var consentEl = el.querySelector('[data-hf-consent]');
      var hpEl = el.querySelector('[name="' + HONEYPOT_NAME + '"]');
      var payload = buildPayload(def, values, {
        consent: consentEl ? !!consentEl.checked : false,
        honeypot: hpEl ? hpEl.value : '',
        utm: readUtm(window.location.search)
      });

      form.setAttribute('data-hf-busy', '1');
      form.classList.add('hf-busy');
      if (button) button.disabled = true;

      fetch(apiBase + '/forms/' + encodeURIComponent(def.slug) + '/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().catch(function () { return null; });
        })
        .then(function (body) {
          if (body && body.ok) {
            var redirect = safeRedirect(def.redirectUrl);
            if (redirect) {
              window.location.assign(redirect);
              return;
            }
            el.innerHTML = '<div class="hf-success" role="status">' +
              esc(def.successMessage || DEFAULT_SUCCESS) + '</div>';
            return;
          }
          var byKey = errorsByKey(body && body.errors);
          paintErrors(el, byKey);
          if (formError && !Object.keys(byKey).length) {
            formError.textContent = 'Something went wrong. Please try again.';
          }
        })
        .catch(function () {
          if (formError) formError.textContent = 'We could not reach the server. Please try again.';
        })
        .then(function () {
          form.removeAttribute('data-hf-busy');
          form.classList.remove('hf-busy');
          if (button) button.disabled = false;
        });
    });
  }

  function init() {
    var nodes = document.querySelectorAll('[data-hitched-form]');
    for (var i = 0; i < nodes.length; i++) mount(nodes[i]);
  }

  window.HitchedForms = { _core: core, mount: mount, init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
